import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as fromApp from '../../../store/app.reducer';
import { Subscription } from 'rxjs';
import { PasswordValidators } from '../../../validators/password.validator';
import { EmployeeModel } from '../../../models/employee.model';
import * as AuthActions from '../../../auth/store/auth.actions';

@Component({
    selector: 'app-change-password-modal',
    templateUrl: './change-password-modal.component.html',
    styleUrls: ['./change-password-modal.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        TranslateModule
    ]
})
export class ChangePasswordModalComponent implements OnInit, OnDestroy {
    public subscription: Subscription;
    passwordForm: FormGroup;
    showCurrentPassword = false;
    showNewPassword = false;
    showConfirmPassword = false;
    isSubmitting = false;
    public employee: EmployeeModel;

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<ChangePasswordModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private store: Store<fromApp.AppState>,
        private translate: TranslateService
    ) {
        this.initForm();
    }

    private initForm(): void {
        this.passwordForm = this.fb.group({
            currentPassword: ['', [
                Validators.required, 
                Validators.minLength(8), 
                PasswordValidators.complexityValidator, 
                this.noWhitespaceValidator
            ]],
            newPassword: ['', [
                Validators.required, 
                Validators.minLength(8), 
                PasswordValidators.complexityValidator, 
                this.noWhitespaceValidator
            ]],
            confirmPassword: ['', [
                Validators.required,
                this.noWhitespaceValidator
            ]]
        }, {
            validators: [this.passwordMatchValidator, this.passwordNotSameValidator]
        });
    }

    private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
        const newPassword = control.get('newPassword');
        const confirmPassword = control.get('confirmPassword');

        if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
            confirmPassword.setErrors({ passwordMismatch: true });
            return { passwordMismatch: true };
        }

        return null;
    }

    // Custom validator to prevent whitespace-only passwords
    private noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
        const isWhitespace = (control.value || '').trim().length === 0;
        const isValid = !isWhitespace;
        return isValid ? null : { whitespace: true };
    }

    private passwordNotSameValidator(control: AbstractControl): ValidationErrors | null {
        const currentPassword = control.get('currentPassword');
        const newPassword = control.get('newPassword');

        if (currentPassword && newPassword && 
            currentPassword.value && newPassword.value &&
            currentPassword.value === newPassword.value) {
            newPassword.setErrors({ ...newPassword.errors, samePassword: true });
            return { samePassword: true };
        }

        // Remove samePassword error if passwords are different
        if (newPassword && newPassword.errors) {
            delete newPassword.errors['samePassword'];
            if (Object.keys(newPassword.errors).length === 0) {
                newPassword.setErrors(null);
            }
        }

        return null;
    }

    ngOnInit(): void {
        this.subscription = this.store.subscribe(appState => {
            this.employee = appState.auth.employee;
        });
        this.setupFormValidation();
    }

    get currentPassword() {
        return this.passwordForm.get('currentPassword')?.value?.trim();
    }

    get newPassword() {
        return this.passwordForm.get('newPassword')?.value?.trim();
    }

    get confirmPassword() {
        return this.passwordForm.get('confirmPassword')?.value?.trim();
    }

    private setupFormValidation(): void {
        this.passwordForm.valueChanges.subscribe(() => {
            if (this.isSubmitting) {
                this.validateForm();
            }
        });
    }

    private validateForm(): void {
        if (this.passwordForm.valid) {
            this.isSubmitting = true;
            return;
        }
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    getErrorMessage(controlName: string): string {
        const control = this.passwordForm.get(controlName);
        if (!control || !control.errors) return '';

        const errors = control.errors;
        const controlNameCapitalized = controlName.charAt(0).toUpperCase() + controlName.slice(1);
        let controlNameTranslated = '';

        if(controlNameCapitalized === 'CurrentPassword') {
            controlNameTranslated = this.translate.instant('profile.current-password');
        } else if(controlNameCapitalized === 'NewPassword') {
            controlNameTranslated = this.translate.instant('profile.new-pass');
        } else if(controlNameCapitalized === 'ConfirmPassword') {
            controlNameTranslated = this.translate.instant('profile.confirm-pass');
        } else {
            controlNameTranslated = controlNameCapitalized;
        }

        // Priority order for displaying errors
        if (errors['required']) {
            return `${controlNameTranslated} ${this.translate.instant('form.validation.required')}`;
        }
        if (errors['whitespace']) {
            return `${controlNameTranslated} ${this.translate.instant('form.validation.no-whitespace')}`;
        }
        if (errors['minlength']) {
            const requiredLength = errors['minlength'].requiredLength;
            return `${controlNameTranslated} ${this.translate.instant('form.validation.min-length', { minLength: requiredLength })}`;
        }
        if (errors['passwordComplexity']) {
            const missing = errors['passwordComplexity'].missing;
            const requirements = [];

            if (missing.upperCase) requirements.push(this.translate.instant('form.validation.uppercase-letter'));
            if (missing.lowerCase) requirements.push(this.translate.instant('form.validation.lowercase-letter'));
            if (missing.numeric) requirements.push(this.translate.instant('form.validation.number'));
            if (missing.specialChar) requirements.push(this.translate.instant('form.validation.special-character'));

            return `${controlNameTranslated} ${this.translate.instant('form.validation.must-include')}: ${requirements.join(', ')}`;
        }
        if (errors['samePassword']) {
            return `${controlNameTranslated} ${this.translate.instant('form.validation.password-must-be-different')}`;
        }
        if (errors['passwordMismatch']) {
            return `${controlNameTranslated} ${this.translate.instant('form.validation.passwords-not-match')}`;
        }

        return '';
    }

    toggleCurrentPasswordVisibility(): void {
        this.showCurrentPassword = !this.showCurrentPassword;
    }

    toggleNewPasswordVisibility(): void {
        this.showNewPassword = !this.showNewPassword;
    }

    toggleConfirmPasswordVisibility(): void {
        this.showConfirmPassword = !this.showConfirmPassword;
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    private markAllFieldsAsTouched(): void {
        Object.keys(this.passwordForm.controls).forEach(key => {
            const control = this.passwordForm.get(key);
            control?.markAsTouched();
        });
    }

    // Helper method to check if form is valid
    get isFormValid(): boolean {
        return this.passwordForm.valid;
    }

    onSubmit(): void {
        // Mark all fields as touched to trigger validation display
        this.markAllFieldsAsTouched();

        if (this.isFormValid && !this.isSubmitting) {
            this.isSubmitting = true;
            
            const passwordFormData = {
                employeeId: this?.employee?.id,
                oldPassword: this.currentPassword,
                password: this.newPassword,
                confirmPassword: this.confirmPassword
            };

            this.store.dispatch(new AuthActions.ChangePassword(passwordFormData));
            
            this.dialogRef.close(true);
            
            // Simulate API delay
            setTimeout(() => {
                this.isSubmitting = false;
                this.dialogRef.close(true);
            }, 1000);
        }
    }
}